import { PrismicNextImage } from '@prismicio/next';
import { asText, Content, isFilled } from '@prismicio/client';

import {
  Card,
  Badge,
  Heading,
  AutoGrid,
  CardMeta,
  CardBody,
  CardHero,
  CardFooter,
  Typography,
  CardContent,
  AutoGridLayout,
  CardFeature,
} from '@preventx/oxygen';
import { RichText } from '@preventx/prismic-services';

// Constants
import { PriceReplacementKey } from '@/constants/configuration';

// Services
import { applicationEnv } from '@/services/env/shared';
import { retrieveProductsConfiguration } from '@/services/pages/products/products';

// Components
import { Link } from '@/components/Actions/Link';

type BaseProductsContainerProps = {
  sliceId: string;
  addRegion: boolean;
  displayImage: boolean;
  products: Content.ProductsSliceDefaultPrimaryProductsItem[];
};

export const BaseProductsContainer = async ({
  sliceId,
  products,
  displayImage,
}: BaseProductsContainerProps) => {
  if (products.length === 0) {
    return null;
  }

  const gridLayout: AutoGridLayout | undefined =
    {
      2: '50-50' as AutoGridLayout,
      3: 'thirds' as AutoGridLayout,
    }[products.length] ?? undefined;

  const items = (await retrieveProductsConfiguration(products)).filter(
    (item) => item !== null,
  );

  const { WebsiteUrl } = applicationEnv();

  return (
    <AutoGrid layout={gridLayout} component="div">
      {items.map(({ id, link, data, price, imageMeta, colourScheme }) => (
        <CardFeature key={`region-product-features-${sliceId}-${id}`}>
          <Card component="article" colour={colourScheme ?? undefined}>
            <CardHero component="div">
              {displayImage && isFilled.image(data.product_image) && (
                <PrismicNextImage field={data.product_image} />
              )}

              {isFilled.richText(imageMeta.label) && (
                <CardMeta>
                  <Badge
                    colour="primary"
                    variant="solid"
                    icon={
                      imageMeta.icon
                        ? { definition: imageMeta.icon, position: 'left' }
                        : undefined
                    }
                  >
                    {asText(imageMeta.label)}
                  </Badge>
                </CardMeta>
              )}
            </CardHero>

            <CardBody>
              <CardContent addFlow>
                <Heading component="h3" className="card__heading">
                  {asText(data.product_name)}
                </Heading>
                <RichText
                  variant="default"
                  content={data.content}
                  websiteUrl={WebsiteUrl}
                />
              </CardContent>
              <CardFooter addRepel>
                <Typography component="span">
                  {asText(data.price_label).replace(
                    PriceReplacementKey,
                    price.toString(),
                  )}
                </Typography>

                <Link
                  href={link}
                  as="default"
                  type="button"
                  variant="solid"
                  retainSearchParams
                  className="card__action"
                >
                  {link.text}
                </Link>
              </CardFooter>
            </CardBody>
          </Card>
        </CardFeature>
      ))}
    </AutoGrid>
  );
};
